// Device Storage Service - Uses File System Access API to save data to device
// This provides persistent storage that survives browser cache clearing

import { mergeRecordArrays, mergeSettings } from '../utils/mergeRecords';

const DATA_TYPES = [
  'employees', 'customers', 'invoices', 'quotes', 'appointments',
  'inventory', 'expenses', 'mileageEntries', 'timeEntries', 'settings'
];

const STORAGE_KEY = 'maidly_device_folder_handle';
const DATA_FILE_NAME = 'maidly-data.json';

class DeviceStorageService {
  constructor() {
    this.directoryHandle = null;
    // The File System Access API is only usable when:
    //   1. the browser exposes showDirectoryPicker (Chrome/Edge; not Safari/FF), AND
    //   2. we're the TOP-LEVEL page — cross-origin iframes (e.g. the Emergent
    //      preview) throw a SecurityError when the picker is invoked.
    this.isSupported = 'showDirectoryPicker' in window && this._isTopLevel();
    this.isConnected = false;
  }

  // True only when not embedded in a (cross-origin) iframe.
  _isTopLevel() {
    try {
      return window.self === window.top;
    } catch {
      // Access to window.top threw -> we're in a cross-origin frame.
      return false;
    }
  }

  // Check if File System Access API is supported
  checkSupport() {
    return this.isSupported;
  }

  // Request folder access from user
  async requestFolderAccess() {
    if (!this.isSupported) {
      throw new Error(
        this._isTopLevel()
          ? 'Your browser does not support folder backup. Use Chrome or Edge on desktop, or use Manual File export/import.'
          : 'Folder backup is unavailable inside the app preview. Open the published app in its own browser tab (Chrome/Edge) to use it.'
      );
    }

    try {
      // Request directory picker
      this.directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents',
        id: 'maidly-data-folder'
      });

      // Store handle for later sessions (requires permission re-grant on reload)
      this.isConnected = true;
      
      // Save indicator that we have a folder configured
      localStorage.setItem(STORAGE_KEY, 'configured');
      localStorage.setItem('maidly_device_folder_name', this.directoryHandle.name);

      return {
        success: true,
        folderName: this.directoryHandle.name
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Folder selection cancelled' };
      }
      if (error.name === 'SecurityError') {
        return {
          success: false,
          error: 'Folder backup is blocked here. Open the published app in its own Chrome/Edge tab to use it.',
        };
      }
      throw error;
    }
  }

  // Check if we have a stored folder reference
  hasStoredFolder() {
    return localStorage.getItem(STORAGE_KEY) === 'configured';
  }

  // Get stored folder name
  getStoredFolderName() {
    return localStorage.getItem('maidly_device_folder_name') || null;
  }

  // Reconnect to previously selected folder (requires user gesture)
  async reconnect() {
    if (!this.isSupported) return { success: false };
    
    try {
      this.directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents',
        id: 'maidly-data-folder'
      });
      
      this.isConnected = true;
      localStorage.setItem('maidly_device_folder_name', this.directoryHandle.name);
      
      return {
        success: true,
        folderName: this.directoryHandle.name
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Save all data to device file
  async saveToDevice() {
    if (!this.directoryHandle || !this.isConnected) {
      throw new Error('No folder connected. Please select a folder first.');
    }

    try {
      // Collect all data from localStorage
      const exportData = {
        savedAt: new Date().toISOString(),
        appVersion: '1.0.0',
        data: {}
      };

      DATA_TYPES.forEach(type => {
        const data = localStorage.getItem(`maidly_${type}`);
        if (data) {
          exportData.data[type] = JSON.parse(data);
        }
      });

      // Get or create file
      const fileHandle = await this.directoryHandle.getFileHandle(DATA_FILE_NAME, { create: true });
      
      // Create writable stream
      const writable = await fileHandle.createWritable();
      
      // Write data
      await writable.write(JSON.stringify(exportData, null, 2));
      await writable.close();

      // Update last save time
      localStorage.setItem('maidly_device_last_save', new Date().toISOString());

      return {
        success: true,
        savedAt: exportData.savedAt,
        itemCount: Object.keys(exportData.data).length
      };
    } catch (error) {
      console.error('Save to device error:', error);
      throw error;
    }
  }

  // Load data from device file
  async loadFromDevice() {
    if (!this.directoryHandle || !this.isConnected) {
      throw new Error('No folder connected. Please select a folder first.');
    }

    try {
      // Try to get the file
      const fileHandle = await this.directoryHandle.getFileHandle(DATA_FILE_NAME);
      const file = await fileHandle.getFile();
      const contents = await file.text();
      const importedData = JSON.parse(contents);

      if (!importedData.data) {
        throw new Error('Invalid data file format');
      }

      return {
        success: true,
        data: importedData.data,
        savedAt: importedData.savedAt,
        itemCount: Object.keys(importedData.data).length
      };
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return { success: false, error: 'No saved data found', notFound: true };
      }
      throw error;
    }
  }

  // Import data from device file into localStorage
  async importFromDevice(mergeMode = true) {
    const result = await this.loadFromDevice();
    
    if (!result.success) {
      return result;
    }

    let itemsImported = 0;

    Object.entries(result.data).forEach(([type, newData]) => {
      const existingData = localStorage.getItem(`maidly_${type}`);

      if (type === 'settings') {
        const existing = existingData ? JSON.parse(existingData) : {};
        localStorage.setItem(`maidly_${type}`, JSON.stringify(mergeSettings(existing, newData)));
        itemsImported++;
      } else if (Array.isArray(newData)) {
        if (mergeMode) {
          // Upsert + last-write-wins: keep local-only records, add new ones,
          // and let the newer copy win on id conflicts. Nothing is deleted.
          const existing = existingData ? JSON.parse(existingData) : [];
          const { merged, added, updated } = mergeRecordArrays(existing, newData);
          localStorage.setItem(`maidly_${type}`, JSON.stringify(merged));
          itemsImported += added + updated;
        } else {
          localStorage.setItem(`maidly_${type}`, JSON.stringify(newData));
          itemsImported += newData.length;
        }
      } else {
        localStorage.setItem(`maidly_${type}`, JSON.stringify(newData));
        itemsImported++;
      }
    });

    return {
      success: true,
      itemsImported,
      savedAt: result.savedAt
    };
  }

  // Disconnect folder
  disconnect() {
    this.directoryHandle = null;
    this.isConnected = false;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('maidly_device_folder_name');
    localStorage.removeItem('maidly_device_last_save');
  }

  // Get connection status
  getStatus() {
    return {
      isSupported: this.isSupported,
      isConnected: this.isConnected,
      folderName: this.isConnected ? this.directoryHandle?.name : this.getStoredFolderName(),
      lastSave: localStorage.getItem('maidly_device_last_save'),
      hasStoredFolder: this.hasStoredFolder()
    };
  }
}

// Export singleton instance
const deviceStorage = new DeviceStorageService();
export default deviceStorage;
